import type { Approval, WorkItemDetail } from '@deskworks/shared-types';
import { CHECKPOINT_APPROVAL, START_WORK_APPROVAL } from '@deskworks/shared-types';
import { Check, ShieldAlert, X } from 'lucide-react';
import { useState } from 'react';
import { JsonBlock } from '@/components/code-block';
import { DecisionOptions } from '@/components/decision-options';
import { Markdown } from '@/components/markdown';
import { RelativeTime } from '@/components/relative-time';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { isEmptyObject } from '@/lib/format';
import { useApiMutation } from '@/lib/hooks';
import { t, tr } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';

/**
 * Prominent card for a pending approval: shows what the agent wants to do, lets the user approve or reject — or, when
 * the agent offers answers, pick one of them (or answer in their own words).
 */
export function PendingApprovalCard({ item, approval }: { item: WorkItemDetail; approval: Approval }) {
  const [comment, setComment] = useState('');
  const [picking, setPicking] = useState<string | null>(null);
  const options = approval.options ?? [];
  const [showPayload, setShowPayload] = useState(false);
  const invalidate = [qk.workItem(item.id), qk.approvalsAll(item.workspaceId), qk.overview(item.workspaceId)];
  // A proposed Work Item that waits for approval before it starts (Desk setting).
  const start = approval.type === START_WORK_APPROVAL;
  // The agent reached the point the Work Item's approval rule names.
  const checkpoint = approval.type === CHECKPOINT_APPROVAL;

  const approve = useApiMutation(() => api.approvals.approve(approval.id, { comment: comment.trim() || undefined }), {
    invalidate,
    success: start ? t('Approved — it starts when it’s its turn.') : options.length ? t('Answer sent — the agent continues.') : t('Approved — the agent will continue.'),
  });
  const pick = useApiMutation((choice: string) => api.approvals.approve(approval.id, { choice, comment: comment.trim() || undefined }), {
    invalidate,
    success: t('Decided — the agent continues.'),
  });
  const reject = useApiMutation(() => api.approvals.reject(approval.id, { comment: comment.trim() || undefined }), {
    invalidate,
    success: start ? t('Rejected — this Work Item won’t start.') : t('Rejected — the agent will be notified.'),
  });
  const busy = approve.isPending || reject.isPending || pick.isPending;

  return (
    <div className="rounded-xl border-2 border-orange-300 bg-orange-50/70 p-5 dark:border-orange-800 dark:bg-orange-950/30">
      <div className="flex items-start gap-3">
        <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-orange-500/15 text-orange-600 dark:text-orange-300">
          <ShieldAlert className="size-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold">
            {start
              ? t('Should this Work Item start?')
              : checkpoint
                ? t('The agent reached your approval point')
                : options.length
                  ? t('The agent needs your decision')
                  : t('The agent needs your approval')}
          </h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {tr('Requested {time}', { time: <RelativeTime value={approval.requestedAt} /> })}
            {approval.type && <> · {tr('Type: {type}', { type: <span className="font-mono">{approval.type}</span> })}</>}
          </p>
          {checkpoint && item.approvalRule && (
            <p className="mt-2 text-xs text-muted-foreground break-words whitespace-pre-wrap">{t('Your rule: {rule}', { rule: item.approvalRule })}</p>
          )}
          {approval.description ? (
            <Markdown text={approval.description} className="mt-3" />
          ) : (
            <p className="mt-3 text-sm">{t('No description given.')}</p>
          )}
          {!start && !isEmptyObject(approval.payload) && (
            <div className="mt-3">
              <button
                type="button"
                className="cursor-pointer text-xs font-medium text-orange-800 hover:underline dark:text-orange-300"
                onClick={() => setShowPayload((s) => !s)}
              >
                {showPayload ? t('Hide details') : t('Show details')}
              </button>
              {showPayload && <JsonBlock value={approval.payload} className="mt-2" maxHeight="max-h-64" />}
            </div>
          )}
          {options.length > 0 && (
            <DecisionOptions
              className="mt-4"
              options={options}
              picking={picking}
              disabled={busy}
              onPick={(choice) => {
                setPicking(choice);
                pick.mutate(choice, { onSettled: () => setPicking(null) });
              }}
            />
          )}
          <Textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={
              options.length
                ? t('Or answer in your own words — or add a note to the option you pick')
                : t('Comment for the agent (optional) — e.g. why you’re rejecting or what it should watch out for')
            }
            rows={2}
            className={options.length ? 'mt-3 bg-card' : 'mt-4 bg-card'}
          />
          <div className="mt-3 flex flex-wrap gap-2">
            {options.length ? (
              <Button variant="outline" onClick={() => approve.mutate()} loading={approve.isPending} disabled={busy || !comment.trim()}>
                {!approve.isPending && <Check />} {t('Send my answer')}
              </Button>
            ) : (
              <Button onClick={() => approve.mutate()} loading={approve.isPending} disabled={busy} className="bg-emerald-600 text-white hover:bg-emerald-600/90">
                {!approve.isPending && <Check />} {t('Approve')}
              </Button>
            )}
            <Button variant="outline" onClick={() => reject.mutate()} loading={reject.isPending} disabled={busy}>
              {!reject.isPending && <X />} {t('Reject')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
